import type { Composer } from 'vue-i18n';
import type { RouteLocationNormalized, Router } from 'vue-router';

/**
 * Chủ sở hữu DUY NHẤT của `document.title`.
 *
 * Trước file này chỉ `pages/error-404/Error404.vue` gọi `useMeta` — nghĩa là tiêu đề tab mặc
 * định là "Booknow" cho tới khi ai đó chạm một URL sai, rồi **kẹt "404 Not Found" vĩnh viễn**:
 * `useMeta` gỡ phần đóng góp của component khi unmount, nhưng không có ai đóng góp tiếp nên
 * chuỗi cuối cùng nằm lại trên thẻ tab suốt phiên. Người dùng ghim tab hoặc mở nhiều tab thấy
 * "404 Not Found" trong khi đang đứng ở màn thu ngân.
 *
 * Cách chữa không phải rải `useMeta` ra từng route, mà là một chủ sở hữu ở tầng router: màn nào
 * muốn tiêu đề riêng thì khai `meta.titleKey`, không khai thì về tên sản phẩm.
 */
// Nguồn: `index.html` là `<title><%= productName %></title>`, và app-vite bơm
// `package.json:productName` vào đó lúc build — nên ngay trước khi hook này chạy lần đầu,
// `document.title` CHÍNH LÀ tên sản phẩm. Chụp một lần là hết drift giữa hai chỗ.
// Fallback cho môi trường không có `document` (SSR) và cho test (jsdom mở ra với title rỗng).
const FALLBACK_PRODUCT_NAME = 'Booknow';
let productName = FALLBACK_PRODUCT_NAME;

function titleFor(i18n: Composer, to: RouteLocationNormalized): string {
  // Duyệt từ record SÂU nhất ra ngoài: con đè cha, giống cách vue-router hiểu meta.
  for (let index = to.matched.length - 1; index >= 0; index -= 1) {
    const key = to.matched[index]?.meta.titleKey;
    if (!key) {
      continue;
    }
    const translated = i18n.t(key);
    // Khoá trượt thì `t()` trả lại chính tên khoá — thà để tên sản phẩm còn hơn dán
    // "notFound.metaTitle" lên tab.
    return translated === key ? productName : `${translated} · ${productName}`;
  }
  return productName;
}

/** Đặt lại tiêu đề cho route đang đứng — gọi sau khi lật ngôn ngữ. */
export function refreshDocumentTitle(i18n: Composer, router: Router): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.title = titleFor(i18n, router.currentRoute.value);
}

/**
 * `afterEach` chứ không phải `beforeEach`: bundle co-locate của màn đích chỉ được merge trong
 * `beforeEach` của `installI18nRouterHooks`, nên đọc khoá sớm hơn là đọc lúc cây message chưa có
 * namespace đó ⇒ tiêu đề ra nguyên tên khoá.
 *
 * ⚠ Tham số thứ ba `failure` KHÔNG được bỏ. Đo trên vue-router 4.6.4 (`vue-router.mjs:1317`):
 * `triggerAfterEach(to, from, failure)` **vẫn chạy** khi điều hướng bị ABORTED/CANCELLED — chỉ
 * nhánh GUARD_REDIRECT thoát sớm. Bỏ qua nó là đặt tiêu đề cho màn CHƯA TỪNG TỚI: `SetupStore`
 * hỏi "chưa lưu", người dùng chọn *Ở lại* ⇒ tab mang tên màn kia và kẹt tới lần điều hướng
 * thành công kế tiếp — đúng lớp lỗi file này sinh ra để diệt.
 */
export function installDocumentTitle(i18n: Composer, router: Router): void {
  if (typeof document !== 'undefined' && document.title.trim()) {
    productName = document.title.trim();
  }
  router.afterEach((to, _from, failure) => {
    if (failure || typeof document === 'undefined') {
      return;
    }
    document.title = titleFor(i18n, to);
  });
}
